<script lang="js" setup>
import { onlyAllowNumber, useLocalePath, watchThrottled } from '#imports'
import { useStorage } from '@vueuse/core'
import Decimal from 'decimal.js-light'
import { useSpotStore } from '~/store/spot'
import { usePanelStore } from '~/store/spot/panel'
import { useUserStore } from '~/store/user'
import { div, fix, fixRaw, mul, percent } from '~/utils/math'
import { usePlaceOrder } from '~/hooks/spot'
import { useBackUrl } from '~/hooks/index.js'

import Sides from './Sides.vue'
import IconAttention from '~/assets/images/spot/ic-attention.svg'
import IconAdd from '~/assets/images/spot/ic-add.svg'
import IconCalculator from '~/assets/images/spot/ic-calculator-s.svg'
import IconChevronDown from '~/assets/images/common/icon-chevron-down.svg'

const ModalOrderConfirm = defineAsyncComponent(() => import('~/components/spot/modal/OrderConfirm.vue'))

const { t } = useI18n()
const spot = useSpotStore()
const panel = usePanelStore()
const user = useUserStore()
const localePath = useLocalePath()
const message = useMessage()
const { placeOrder } = usePlaceOrder()
const modalOrderConfirmOpen = ref(false)
const orderConfirm = useStorage('spotOrderConfirm')

const types = computed(() => [
  { title: t('限价'), key: 'LIMIT', tip: t('限价委托指使用指定价格进行买卖,限价委托不能保持一定成交') },
  { title: t('市价'), key: 'MARKET', tip: t('市价委托使用目前市场最优价格进行买卖') },
])
const priceTypes = computed(() => [
  { label: t('价格'), value: 'DEFAULT' },
  { label: t('排队价'), value: 'QP', tip: [t('排队价：'), t('排队价就是买一价买，卖一价卖。'), t('买入时，以买一价挂单;'), t('卖出时，以卖一价挂单;')] },
  { label: t('对手价'), value: 'CP', tip: [t('对手价：'), t('如果您买入，则对手价为卖一价;'), t('如果您卖出，则对手价是买一价。')] },
])
const priceType = computed(() => priceTypes.value.find(i => i.value === panel.priceType) ?? {})
const marks = {
  0: '0%',
  25: '25%',
  50: '50%',
  75: '75%',
  100: '100%',
}

const wallet = computed(() => {
  const code = panel.isBuy ? spot.quote : spot.base
  const dp = panel.isBuy ? spot.ps : spot.as
  return Object.assign({ dp }, spot.getBalanceByCode(code))
})

const btnDisabled = computed(() => {
  const factor = panel.isBuy ? panel.amount : panel.qty

  const valid = panel.isMarket ? factor : factor && panel.getPrice

  return !valid
})

watchThrottled(() => panel.slider, nv => handlerSlider(nv), { throttle: 100 })
watch([() => panel.priceType, () => panel.side, () => panel.orderType, () => spot.code], () => {
  reset()
})

function handlerSlider(nv) {
  if (!nv)
    return reset()
  const k = panel.isBuy ? 'amount' : 'qty'

  const { dp, trade } = wallet.value
  panel[k] = fixRaw(mul(trade, nv).div(100), dp)

  if (!panel.isMarket) {
    panel.isBuy ? syncQtyByAmt() : syncAmtByQty()
  }
}

function handlerPrice(v) {
  panel.price = v

  if (!panel.isMarket) {
    panel.qty && syncAmtByQty()
    panel.amount && syncQtyByAmt()
  }
}

function handlerAmt(v) {
  panel.amount = v
  !panel.isMarket && syncQtyByAmt(v)
}

function handlerQty(v) {
  panel.qty = v
  !panel.isMarket && syncAmtByQty(v)
}

function syncAmtByQty(v = panel.qty) {
  if (!v)
    return panel.amount = ''

  const price = panel.getPrice
  if (price)
    panel.amount = fixRaw(mul(v, price), spot.ps)
}

function syncQtyByAmt(v = panel.amount) {
  if (!v)
    return panel.qty = ''

  const price = panel.getPrice
  if (price)
    panel.qty = fixRaw(div(v, price), spot.as)
}

function reset() {
  panel.price = ''
  panel.slider = 0
  panel.amount = ''
  panel.qty = ''
}

function handlerPlace() {
  if (+orderConfirm.value) {
    modalOrderConfirmOpen.value = true
    return
  }
  place()
}

async function place() {
  const { msg } = validate()
  if (msg) {
    return message.error(msg)
  }

  const { success } = await placeOrder()
  if (success)
    reset()

  return success
}

function validate() {
  const k = panel.isBuy ? 'amount' : 'qty'
  const { limitMinVolume, limitMinPrice } = spot.curInfo
  let msg = ''
  if (new Decimal(panel[k]).gt(wallet.value.trade) || +wallet.value.trade <= 0) {
    msg = t('可用余额不足')
  }
  else if (panel.isLimit) {
    if (new Decimal(panel.getPrice).lt(limitMinPrice)) {
      msg = t('限价委托,价格不能小于 {0}', [limitMinVolume])
    }
    else if (new Decimal(panel.qty).lt(limitMinVolume)) {
      msg = t('限价委托，数量不能小于 {0}', [limitMinVolume])
    }
  }

  return { msg }
}
</script>

<template>
  <div class="min-h-[488px] bg-fillPagePrimary px-3 pt-[10px]">
    <ClientOnly>
      <Sides />
      <common-x-mod :tab="panel.orderType" :tabs="types" header-class="h-[30px] !px-0" class="pt-2" @tab-change="v => panel.orderType = v">
        <template #actions="row">
          <common-x-tips-simple :text="row.tip">
            <IconAttention class="h-4 w-4 text-textPrimary cursor-pointer" />
          </common-x-tips-simple>
        </template>
      </common-x-mod>
      <div class="pt-3 flex flex-col">
        <div v-if="panel.isMarket" class="h-10 rounded-md bg-inputHovered text-xs text-textTertiary px-3 flex items-center cursor-not-allowed">
          {{ t('市价') }}
        </div>
        <n-input v-else v-model:value="panel.price" size="large" style="--n-height: 40px;--n-color-disabled:var(--fillInputHovered)" class="text-xs font-medium text-end !transition-none" :disabled="!panel.isDefaultPt" :allow-input="(value) => onlyAllowNumber(value, spot.ps)" :on-update:value="handlerPrice" placeholder="">
          <template #prefix>
            <common-x-tips-simple :text="priceType.tip" :disabled="!priceType.tip">
              <div class="text-textTertiary leading-4" :class="{ 'border-b border-dashed border-textTertiary': !panel.isDefaultPt }">
                {{ priceType.label }}
              </div>
            </common-x-tips-simple>
          </template>
          <template #suffix>
            <n-popselect v-model:value="panel.priceType" trigger="click" :options="priceTypes" placement="bottom-end" :show-checkmark="false">
              <button class="py-1 bg-transparent">
                <IconChevronDown class="W-4 h-4 text-textPrimary" />
              </button>
            </n-popselect>
          </template>
        </n-input>
        <n-input v-if="!(panel.isMarket && panel.isBuy)" v-model:value="panel.qty" style="--n-height: 40px" size="large" class="text-xs font-medium text-end mt-2 !transition-none" :allow-input="(value) => onlyAllowNumber(value, spot.as)" :on-update:value="handlerQty" placeholder="">
          <template #prefix>
            <div class="text-textTertiary">
              {{ t('数量') }}
            </div>
          </template>
          <template #suffix>
            <div>{{ spot.base }}</div>
          </template>
        </n-input>
        <n-input v-if="!(panel.isMarket && !panel.isBuy)" v-model:value="panel.amount" style="--n-height: 40px" :allow-input="(value) => onlyAllowNumber(value, spot.ps)" :on-update:value="handlerAmt" size="large" class="text-xs font-medium mt-2 order-1 text-end !transition-none" :class="{ '!order-3': !panel.isMarket }" placeholder="">
          <template #prefix>
            <div class="text-textTertiary">
              {{ t('成交额') }}
            </div>
          </template>
          <template #suffix>
            <div>{{ spot.quote }}</div>
          </template>
        </n-input>
        <common-x-slider v-model:value="panel.slider" class="!mt-2 order-2" :marks="marks" :step="1" :min="0" :max="100" />
      </div>
      <div class="flex items-center justify-between mt-4">
        <div class="text-[10px] text-textSecondary">
          {{ t('可用') }}
          <em class="w-4 text-textPrimary">{{ fix(wallet.trade ?? '-', 2) }} {{ wallet.code }}</em>
        </div>
        <a :href="localePath(`/assets/deposit/${spot.quote}`)">
          <IconAdd class="h-4 w-4 text-textPrimary cursor-pointer" />
        </a>
      </div>
      <div class="flex items-center text-[10px] text-textSecondary mt-1">
        <IconCalculator class="h-4 w-4 text-textPrimary me-1" />
        <common-x-tips-simple>
          <template #content>
            <div>
              <p>{{ t('现货交易手续费:') }}</p>
              <p>{{ t('您在交易代币时由交易所收取手续费。') }}</p>
              <p>{{ t('手续费费率:') }}</p>
              <p>Maker: {{ percent(spot.curInfo?.makerFeeRate, 2, false) }}</p>
              <p>Taker: {{ percent(spot.curInfo?.takerFeeRate, 2, false) }}</p>
              <p>{{ t('手续费公式:') }}</p>
              <p>{{ t('手续费=已成交数量x手续费费率') }}</p>
              <p>{{ t('特别说明:') }}</p>
              <p>{{ t('手续费以获得的币种收取。买入时收取基准货币,卖出时收取计价货币。撤销的订单无需手续费。') }}</p>
            </div>
          </template>
          <span class="leading-[14px] border-b border-dashed border-textSecondary">{{ t('手续费率') }}</span>
        </common-x-tips-simple>
      </div>
      <common-x-button v-if="user.isLogin" :type="panel.isBuy ? 'buy' : 'sell'" :disabled="btnDisabled" class="w-full mt-4 [&:hover:not(:disabled)]:opacity-70 transition" @click="handlerPlace">
        {{ panel.isBuy ? t('买入 {0}', [spot.base]) : t('卖出 {0}', [spot.base]) }}
      </common-x-button>
      <template v-else>
        <common-x-button class="w-full mt-4">
          <a class="w-full h-full flex items-center justify-center" :href="useBackUrl(localePath('/log-in'))">
            {{ t('登录') }}
          </a>
        </common-x-button>
        <common-x-button class="w-full mt-3" type="tertiary">
          <a class="w-full h-full flex items-center justify-center" :href="useBackUrl(localePath('/sign-up'))">
            {{ t('注册') }}
          </a>
        </common-x-button>
      </template>
      <ModalOrderConfirm v-model:show="modalOrderConfirmOpen" :ok="place" />
    </ClientOnly>
  </div>
</template>
